import React from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import styles from '@jobcase/react_pattern_library/styles/rebrand';

import NavigationHeader from '../../../headers/NavigationHeader/NavigationHeaderRebrand';

import { getIsNavCollapsed } from '../../../redux/nodes/application/selectors';

import { useSelector } from '../../../utils/hooks';
import { TRANSITION_DELAY } from '../../../utils/constants/employerNavigation';

const NavAndMainContainer = styled.div`
  flex: ${({ navAndMainFlex }) => navAndMainFlex}; /* This is needed for the footer to stick to the bottom of the page */
  min-height: ${({ navAndMainMinHeight }) => navAndMainMinHeight};
`;

const MainContainer = styled.main`
  transition: padding ${TRANSITION_DELAY}s;
  ${({ backgroundColor }) => backgroundColor && css`
    background-color: ${backgroundColor};
  `}

  @media(min-width: ${styles.breakpoint.md}) {
    padding-left: ${({ showSidebar, isCollapsed }) => {
    if (isCollapsed) { return `${styles.spacing.large}px`; }
    if (showSidebar && !isCollapsed) { return `${240}px`; }
    return '0';
  }}
  }
`;

const ContentWrapper = styled.div`
  padding: ${styles.spacing.tiny}px;
  padding-top: 0;

  ${({ pathname }) => ((pathname) && css`
    background-color: ${styles.color.backgroundGrayColor};
    padding-top: 0;
  `)}

  @media(min-width: ${styles.breakpoint.md}) {
    padding: ${styles.spacing.tiny}px ${styles.spacing.medium}px;
    padding-top: 0;

    ${({ pathname }) => ((pathname) && css`
      padding-top: 0;
    `)}
  }
`;

const PageHeader = styled.div`
  background-color: ${styles.color.jobcaseWhiteColor};
  border-bottom: 1px solid ${styles.color.dividerGrayColor};
  padding: ${styles.spacing.tiny}px;
  position: sticky;
  top: 60px;
  z-index: 1;

  @media(min-width: ${styles.breakpoint.md}) {
    padding: ${styles.spacing.small}px ${styles.spacing.medium}px ${styles.spacing.tiny}px;
    top: 0;
  }
`;

const StandardLayout = ({
  children,
  navAndMainFlex,
  navAndMainMinHeight,
  bodyMarginTop,
  backgroundColor,
  showSidebar,
  showAccountSwitcher,
  showProfileMenu,
  showSecondaryNavigation,
  onClickBack,
  backTitle,
  pageHeader,
  className,
}) => {
  const router = useRouter();
  const pathname = router?.pathname?.endsWith('hiring-events/[id]') || router?.pathname?.endsWith('hiring-events');
  const isCollapsed = useSelector(getIsNavCollapsed);

  return (
    <>
      <NavAndMainContainer
        navAndMainFlex={navAndMainFlex}
        navAndMainMinHeight={navAndMainMinHeight}
        className={className}
      >
        <NavigationHeader
          showAccountSwitcher={showAccountSwitcher}
          showProfileMenu={showProfileMenu}
          showSidebar={showSidebar}
          showSecondaryNavigation={showSecondaryNavigation}
          onClickBack={onClickBack}
          backTitle={backTitle}
        />
        <MainContainer
          isCollapsed={isCollapsed}
          backgroundColor={backgroundColor}
          bodyMarginTop={bodyMarginTop}
          showSidebar={showSidebar}
        >
          {pageHeader && (
            <PageHeader className="page-header" id="page-header">
              {pageHeader}
            </PageHeader>
          )}
          <ContentWrapper pathname={pathname} className="main-content-wrapper">
            {children}
          </ContentWrapper>
        </MainContainer>
      </NavAndMainContainer>
    </>
  );
};

StandardLayout.propTypes = {
  /**
   * Children to display
   */
  children: PropTypes.element,
  /**
   * top margin for BodyContainer
   */
  bodyMarginTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * background color for BodyContainer
   */
  backgroundColor: PropTypes.string,
  /**
   * flex for NavAndBodyContainer
   */
  navAndMainFlex: PropTypes.string,
  /**
   * min height for NavAndBodyContainer
   */
  navAndMainMinHeight: PropTypes.string,
  /**
   * showSidebar for showing the sidebar
   */
  showSidebar: PropTypes.bool,
  /**
   * showAccountSwitcher for showing the account switcher
   */
  showAccountSwitcher: PropTypes.bool,
  /**
   * showProfileMenu for showing the profile menu
   */
  showProfileMenu: PropTypes.bool,
  showSecondaryNavigation: PropTypes.bool,
  onClickBack: PropTypes.func,
  backTitle: PropTypes.string,
  pageHeader: PropTypes.element,
  className: PropTypes.string,
};

StandardLayout.defaultProps = {
  children: null,
  bodyMarginTop: '16px',
  backgroundColor: null,
  navAndMainFlex: '1 0 auto',
  navAndMainMinHeight: 'unset',
  showSidebar: true,
  showAccountSwitcher: true,
  showProfileMenu: true,
  showSecondaryNavigation: false,
  onClickBack: null,
  backTitle: null,
  pageHeader: null,
  className: '',
};

export default StandardLayout;
